import React, { useState } from "react";
import { Grid } from "@mui/material";
import {
  Button,
  FormError,
  FormRow,
  FormRowItem,
  OTPInput,
  OutlinedContainer,
} from "@tphglobal/components";
import { brand, greyScaleColour } from "@tphglobal/common/theme/style.palette";
import { resendSignUpCode, resetPassword } from "aws-amplify/auth";
import messages from "../../messages";
import { required } from "../../utils";
import { StyledText } from "@tphglobal/components/loader/styles";
import PhoneImg from "../../assets/images/phone.png";
import SmsImg from "../../assets/images/sms.png";
import { breakpoints } from "@tphglobal/common/theme/style.layout";
import useWindowSize from "@tphglobal/common/hooks/windowSize";

interface Props {
  onCancel: () => void;
  onSubmit: (confirmationCode: string) => void;
  error: string;
  setError: (error: string) => void;
  username: string;
  forgotPassword?: boolean;
  isResend?: boolean;
  signUpModalMsg?: boolean;
  phone?: string;
  isMfa?: boolean;
  isSms?: boolean;
}

const ConfirmOtpForm: React.FC<Props> = ({
  error,
  setError,
  onCancel,
  onSubmit,
  username,
  forgotPassword,
  isResend = true,
  signUpModalMsg = false,
  phone,
  isMfa = false,
  isSms = false,
}) => {
  const [otp, setOtp] = React.useState("");
  const { width } = useWindowSize();
  const isMobile = width <= breakpoints.mobile;

  const handleChange = (otp: string) => {
    setOtp(otp);
  };

  const handleOtpSubmit = () => {
    if (otp?.length < 6) {
      setError("Sorry, we're having trouble verifying your code. Please try again.");
    } else {
      onSubmit(otp);
    }
  };

  const handleResendCode = async () => {
    if (forgotPassword) {
      await resetPassword({ username });
    } else {
      await resendSignUpCode({ username });
    }
  };

  return (
    <>
      <Grid padding="0 24px">
        {isMfa && (
          <Grid container direction="column" alignItems="center">

            <StyledText style={{ textAlign: "center", marginBottom: "16px" }}>
              <strong>Open your Authenticator app</strong> on your phone
              <br />
              <span style={{ fontStyle: "italic" }}>
                (e.g. Microsoft Authenticator, Google Authenticator)
              </span>
              <br /><br />
              and enter the 6-digit code shown for Freelance Portal.
            </StyledText>

            <img
              src={PhoneImg}
              alt="Authenticator app illustration"
              style={{
                width: "220px",
                marginBottom: "24px",
              }}
            />

            <Grid container justifyContent="center">
              <Grid item width="fit-content">
                <StyledText style={{
                  fontWeight: 600,
                  marginBottom: "12px"
                }}>
                  Authentication code (6 digits)
                </StyledText>
              </Grid>
            </Grid>

            <FormRow justifyContent="center" marginBottom="12px">
              <OTPInput
                otp={otp}
                setOtp={setOtp}
                error={error}
                onChange={handleChange}
                onEnter={handleOtpSubmit}
              />
            </FormRow>

            <Grid container justifyContent="center">
              <Grid item width="fit-content">
                <StyledText style={{
                  fontSize: "13px",
                  marginTop: "6px"
                }}>
                  The code refreshes every 30 seconds.
                </StyledText>
              </Grid>
            </Grid>
          </Grid>
        )}

        {isSms && (
          <Grid container direction="column" alignItems="center">

            <StyledText style={{ textAlign: "center", marginBottom: "16px" }}>
              <strong>Check you phone for a text message</strong>
              <br/>
              (A 6-digit code has been sent to your mobile number)
            </StyledText>

            <img
              src={SmsImg}
              alt="SMS illustration"
              style={{
                width: "220px",
                marginBottom: "24px",
              }}
            />

            <Grid container justifyContent="center">
              <Grid item width="fit-content">
                <StyledText style={{
                  fontWeight: 600,
                  marginBottom: "12px"
                }}>
                  Enter the 6-digit code from your SMS
                </StyledText>
              </Grid>
            </Grid>
          </Grid>
        )}

        {!isResend && !isMfa && !isSms && (<FormRow width={"450px"}>
          <StyledText>
            Enter the one-time password (OTP) generated in your authenticator app below:
          </StyledText>
        </FormRow>)}
        {signUpModalMsg && (
          <FormRow width={isMobile ? "100%" : "450px"}>
            <StyledText style={{ wordBreak: "break-word" }}>
              Please enter your OTP Code that has been sent to your device at{" "}
              <span style={{ whiteSpace: "nowrap" }}>{phone}</span>
            </StyledText>
          </FormRow>
        )}
        {!isMfa && (
          <FormRow justifyContent="center">
            <OTPInput
              otp={otp}
              setOtp={setOtp}
              error={error}
              onChange={handleChange}
              onEnter={handleOtpSubmit}
            />
          </FormRow>
        )}
      </Grid>

      <OutlinedContainer
        noBorder
        borderRadius="0"
        padding="14px 24px"
        styles={{ backgroundColor: greyScaleColour?.grey60, marginTop: "24px" }}
      >
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  onClick={() => onCancel()}
                  variant="outlined"
                  buttonColor={brand?.black}
                  color="primary"
                  size="large"
                  label={messages?.general?.cancel}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  label="Confirm"
                  onClick={handleOtpSubmit}
                />
              </Grid>
              {isResend && (
                <Grid item>
                  <Button
                    onClick={handleResendCode}
                    variant="outlined"
                    buttonColor={brand?.black}
                    color="primary"
                    size="large"
                    label="Resend"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </OutlinedContainer>
    </>
  );
};

export default ConfirmOtpForm;
